import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Instagram, Download, Copy, ExternalLink, Loader2 } from "lucide-react";

const CANVAS_SIZE = 1080;
const STORY_W = 1080, STORY_H = 1920;

function wrapText(ctx, text, maxWidth) {
  const words = (text || "").split(/\s+/).filter(Boolean);
  const lines = [];
  let line = "";
  words.forEach((w) => {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line); line = w;
    } else { line = test; }
  });
  if (line) lines.push(line);
  return lines;
}

async function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(new Error(`Image failed to load: ${src}`));
    img.src = src;
  });
}

function resolveImageSrc(src) {
  /**
   * Smart URL resolver:
   *  - /api/uploads/... (our own backend): use directly, no proxy needed
   *  - Any other http(s) URL: route through /api/image/proxy to bypass CORS (works for whitelisted hosts)
   *  - Already-absolute backend uploads URL: use directly
   */
  if (!src) return null;
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  // Self-hosted upload (relative or absolute) → use directly, full CORS already set on /api/uploads/*
  if (src.includes("/api/uploads/")) return src.startsWith("http") ? src : `${apiBase}${src}`;
  if (src.startsWith(apiBase)) return src;
  // External URL → proxy
  return `${apiBase}/api/image/proxy?url=${encodeURIComponent(src)}`;
}

function drawCoverFill(ctx, img, cw, ch) {
  const r = Math.max(cw / img.width, ch / img.height);
  const w = img.width * r;
  const h = img.height * r;
  ctx.drawImage(img, (cw - w) / 2, (ch - h) / 2, w, h);
}

function formatWhen(iso) {
  if (!iso) return "DATE & TIME TBA";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return `${date} · ${time}`;
  } catch { return iso; }
}

export default function ShareFlyerToSocial({ open, onOpenChange, meetup }) {
  const feedCanvasRef = useRef(null);
  const storyCanvasRef = useRef(null);
  const [generating, setGenerating] = useState(false);
  const [renderError, setRenderError] = useState(null);
  const [format, setFormat] = useState("feed");
  const [caption, setCaption] = useState("");

  const meetupUrl = meetup ? `https://betterthroughboxing.com/meetups/${meetup.slug}` : "";

  useEffect(() => {
    if (!meetup) return;
    const free = !meetup.price_cents || meetup.price_cents === 0;
    const price = free ? "FREE" : `$${(meetup.price_cents / 100).toFixed(0)}`;
    const hashtags = [
      "#betterthroughboxing", "#boxing", "#boxingmeetup", "#communityboxing",
      "#flowstatemethod", "#boxinglife", "#freeboxingclass", "#nj",
      ...(meetup.keywords || []).slice(0, 4).map((k) => "#" + k.toLowerCase().replace(/[^a-z0-9]+/g, "")),
    ].filter(Boolean).slice(0, 12).join(" ");
    setCaption(
      `${meetup.title} (${price})\n\n` +
      (meetup.subtitle ? `${meetup.subtitle}\n\n` : "") +
      `📍 ${meetup.location_name || "Location TBA"}\n📅 ${formatWhen(meetup.starts_at)}\n\n` +
      `Register now ↓\n${meetupUrl}\n\n` +
      hashtags
    );
  }, [meetup, meetupUrl]);

  useEffect(() => {
    if (!open || !meetup || !meetup.cover_image) return;
    let cancelled = false;
    setGenerating(true);
    setRenderError(null);

    (async () => {
      try {
        const resolved = resolveImageSrc(meetup.cover_image);
        const img = await loadImage(resolved);
        if (cancelled) return;
        const free = !meetup.price_cents || meetup.price_cents === 0;
        const priceLabel = free ? "FREE" : `$${(meetup.price_cents / 100).toFixed(0)}`;
        const whenLabel = formatWhen(meetup.starts_at);

        // FEED 1080x1080
        const fc = feedCanvasRef.current;
        if (fc) {
          fc.width = CANVAS_SIZE; fc.height = CANVAS_SIZE;
          const ctx = fc.getContext("2d");
          ctx.fillStyle = "#050505";
          ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
          drawCoverFill(ctx, img, CANVAS_SIZE, CANVAS_SIZE);
          // bottom gradient
          const grad = ctx.createLinearGradient(0, CANVAS_SIZE * 0.3, 0, CANVAS_SIZE);
          grad.addColorStop(0, "rgba(5,5,5,0)");
          grad.addColorStop(0.55, "rgba(5,5,5,0.75)");
          grad.addColorStop(1, "rgba(5,5,5,0.97)");
          ctx.fillStyle = grad;
          ctx.fillRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
          // Top-right price/free chip
          ctx.fillStyle = "#FF331F";
          const chipW = priceLabel === "FREE" ? 200 : 220;
          ctx.fillRect(CANVAS_SIZE - chipW - 60, 60, chipW, 64);
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 36px 'Oswald', 'Impact', sans-serif";
          ctx.textAlign = "center";
          ctx.fillText(priceLabel, CANVAS_SIZE - 60 - chipW / 2, 105);
          ctx.textAlign = "left";
          // Red accent bar
          ctx.fillStyle = "#FF331F";
          ctx.fillRect(60, CANVAS_SIZE - 420, 8, 260);
          // Eyebrow
          ctx.fillStyle = "#FF331F";
          ctx.font = "700 22px 'Outfit', system-ui, sans-serif";
          ctx.fillText("COMMUNITY MEETUP", 92, CANVAS_SIZE - 400);
          // Title
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 80px 'Oswald', 'Impact', sans-serif";
          const lines = wrapText(ctx, (meetup.title || "").toUpperCase(), CANVAS_SIZE - 184);
          let y = CANVAS_SIZE - 340;
          lines.slice(0, 3).forEach((ln) => { ctx.fillText(ln, 92, y); y += 86; });
          // When/where
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 26px 'Outfit', system-ui, sans-serif";
          ctx.fillText(whenLabel, 92, CANVAS_SIZE - 170);
          ctx.fillStyle = "#d4d4d8";
          ctx.font = "500 22px 'Outfit', system-ui, sans-serif";
          const locLines = wrapText(ctx, `📍 ${meetup.location_name || "Location TBA"}`, CANVAS_SIZE - 184);
          let ly = CANVAS_SIZE - 138;
          locLines.slice(0, 2).forEach((ln) => { ctx.fillText(ln, 92, ly); ly += 32; });
          // Bottom brand
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 20px 'Outfit', system-ui, sans-serif";
          ctx.fillText("BETTERTHROUGHBOXING.COM/MEETUPS", 92, CANVAS_SIZE - 60);
          ctx.fillStyle = "#a1a1aa";
          ctx.font = "500 18px 'Outfit', system-ui, sans-serif";
          ctx.fillText("@flow_state_method", 92, CANVAS_SIZE - 34);
        }

        // STORY 1080x1920
        const sc = storyCanvasRef.current;
        if (sc) {
          sc.width = STORY_W; sc.height = STORY_H;
          const ctx = sc.getContext("2d");
          ctx.fillStyle = "#050505";
          ctx.fillRect(0, 0, STORY_W, STORY_H);
          const topH = Math.round(STORY_H * 0.58);
          drawCoverFill(ctx, img, STORY_W, topH);
          const tgrad = ctx.createLinearGradient(0, topH * 0.4, 0, topH);
          tgrad.addColorStop(0, "rgba(5,5,5,0)");
          tgrad.addColorStop(1, "rgba(5,5,5,0.9)");
          ctx.fillStyle = tgrad;
          ctx.fillRect(0, 0, STORY_W, topH);
          // top-right chip
          ctx.fillStyle = "#FF331F";
          const chipW = priceLabel === "FREE" ? 240 : 260;
          ctx.fillRect(STORY_W - chipW - 80, 80, chipW, 84);
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 48px 'Oswald', 'Impact', sans-serif";
          ctx.textAlign = "center";
          ctx.fillText(priceLabel, STORY_W - 80 - chipW / 2, 140);
          ctx.textAlign = "left";

          // lower content
          ctx.fillStyle = "#FF331F";
          ctx.fillRect(80, topH + 40, 8, 240);
          ctx.fillStyle = "#FF331F";
          ctx.font = "700 26px 'Outfit', system-ui, sans-serif";
          ctx.fillText("COMMUNITY MEETUP", 116, topH + 80);
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 96px 'Oswald', 'Impact', sans-serif";
          const slines = wrapText(ctx, (meetup.title || "").toUpperCase(), STORY_W - 232);
          let sy = topH + 170;
          slines.slice(0, 4).forEach((ln) => { ctx.fillText(ln, 116, sy); sy += 104; });
          // When/where
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 36px 'Outfit', system-ui, sans-serif";
          ctx.fillText(whenLabel, 116, sy + 40);
          ctx.fillStyle = "#d4d4d8";
          ctx.font = "500 30px 'Outfit', system-ui, sans-serif";
          const locLines = wrapText(ctx, `📍 ${meetup.location_name}`, STORY_W - 232);
          let ly2 = sy + 90;
          locLines.slice(0, 2).forEach((ln) => { ctx.fillText(ln, 116, ly2); ly2 += 42; });
          // CTA box
          ctx.fillStyle = "#FF331F";
          ctx.fillRect(80, STORY_H - 300, STORY_W - 160, 110);
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 50px 'Oswald', 'Impact', sans-serif";
          ctx.textAlign = "center";
          ctx.fillText("REGISTER NOW", STORY_W / 2, STORY_H - 225);
          ctx.textAlign = "left";
          ctx.fillStyle = "#FFFFFF";
          ctx.font = "700 26px 'Outfit', system-ui, sans-serif";
          ctx.textAlign = "center";
          ctx.fillText("BETTERTHROUGHBOXING.COM/MEETUPS", STORY_W / 2, STORY_H - 140);
          ctx.fillStyle = "#a1a1aa";
          ctx.font = "500 22px 'Outfit', system-ui, sans-serif";
          ctx.fillText("@flow_state_method", STORY_W / 2, STORY_H - 100);
          ctx.textAlign = "left";
        }
      } catch (err) {
        const isExternal = meetup.cover_image && !meetup.cover_image.includes("/api/uploads/");
        const msg = isExternal
          ? "Couldn't load this external image (host probably blocks hotlinking, or the URL is invalid). Go back to the meetup editor and re-upload the flyer via the Upload tab — that's hosted on our domain and will always work."
          : "Couldn't render the flyer. The image may be corrupted or too large. Try re-uploading.";
        setRenderError(msg);
        toast.error("Flyer generation failed — see details below");
      } finally {
        if (!cancelled) setGenerating(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, meetup, format]);

  const download = () => {
    const canvas = format === "feed" ? feedCanvasRef.current : storyCanvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return toast.error("Couldn't generate image");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `btb-meetup-${meetup.slug}-${format}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Image saved — ready to post");
    }, "image/png");
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(caption);
      toast.success("Caption copied to clipboard");
    } catch {
      toast.error("Couldn't copy. Select and copy manually.");
    }
  };

  if (!meetup) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border-[#27272A] text-white max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="share-flyer-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase flex items-center gap-2"><Instagram className="h-5 w-5 text-[#FF331F]" /> Share flyer</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs uppercase tracking-widest">
            Generate a branded Instagram/Facebook-ready card from your flyer
          </DialogDescription>
        </DialogHeader>

        <Tabs value={format} onValueChange={setFormat} className="mt-2">
          <TabsList className="bg-[#050505] border border-[#27272A] rounded-sm">
            <TabsTrigger value="feed" className="data-[state=active]:bg-[#FF331F] data-[state=active]:text-white uppercase tracking-widest text-xs" data-testid="flyer-feed-tab">Feed (1:1)</TabsTrigger>
            <TabsTrigger value="story" className="data-[state=active]:bg-[#FF331F] data-[state=active]:text-white uppercase tracking-widest text-xs" data-testid="flyer-story-tab">Story (9:16)</TabsTrigger>
          </TabsList>

          {renderError && (
            <div className="mt-4 border border-amber-700 bg-amber-950/30 text-amber-200 p-4 text-xs leading-relaxed" data-testid="flyer-error">
              <strong className="block mb-1">Image issue.</strong>
              {renderError}
              {meetup?.cover_image && (
                <a href={meetup.cover_image} target="_blank" rel="noreferrer" className="block mt-2 text-amber-100 underline break-all">{meetup.cover_image}</a>
              )}
            </div>
          )}

          <TabsContent value="feed" className="mt-4">
            <div className="relative bg-[#050505] border border-[#27272A] mx-auto" style={{ maxWidth: 420 }}>
              {generating && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
                  <Loader2 className="h-6 w-6 animate-spin text-[#FF331F]" />
                </div>
              )}
              <canvas ref={feedCanvasRef} className="w-full h-auto block" data-testid="flyer-feed-canvas" />
            </div>
          </TabsContent>
          <TabsContent value="story" className="mt-4">
            <div className="relative bg-[#050505] border border-[#27272A] mx-auto" style={{ maxWidth: 280 }}>
              {generating && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10">
                  <Loader2 className="h-6 w-6 animate-spin text-[#FF331F]" />
                </div>
              )}
              <canvas ref={storyCanvasRef} className="w-full h-auto block" data-testid="flyer-story-canvas" />
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-4 space-y-2">
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Caption</div>
          <Textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={8} className="bg-[#050505] border-[#27272A] rounded-sm font-mono text-xs" data-testid="flyer-caption" />
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t border-[#27272A] mt-4">
          <Button type="button" variant="outline" onClick={copyCaption} className="border-[#27272A] bg-[#050505] text-white hover:bg-[#121212] rounded-sm font-bold uppercase tracking-widest" data-testid="flyer-copy-caption-btn">
            <Copy className="h-4 w-4 mr-2" /> Copy caption
          </Button>
          <Button type="button" onClick={download} disabled={generating} className="bg-white text-[#050505] hover:bg-zinc-200 rounded-sm font-bold uppercase tracking-widest" data-testid="flyer-download-btn">
            <Download className="h-4 w-4 mr-2" /> Download {format === "feed" ? "feed" : "story"}
          </Button>
          <a
            href="https://www.instagram.com/flow_state_method/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-sm px-4 py-2 rounded-sm"
            data-testid="flyer-open-instagram-btn"
          >
            <Instagram className="h-4 w-4" /> Open Instagram <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </DialogContent>
    </Dialog>
  );
}
